import React from "react";

import { cn } from "@/lib/utils";
import type { Localizer } from "@/main/react/Types";
import SubjectCombobox from "@/main/react/discussion/facet/SubjectCombobox";
import HalfStarRatingInput from "@/main/react/discussion/facet/HalfStarRatingInput";
import type { ReviewFacetState, SubjectSelection } from "@/main/react/discussion/facet/ReviewFacetTypes";

export type ReviewFacetFieldsProps = {
    localizer: Localizer;
    value: ReviewFacetState;
    onChange: (next: ReviewFacetState) => void;
    /** The group the author is publishing into — a ranking signal for the typeahead, never a filter. */
    groupPublicId?: string;
    disabled?: boolean;
    /** Server-side publish errors, keyed exactly as the backend reports them. */
    subjectErrors?: string[];
    ratingErrors?: string[];
    /** Rendered only where an editor exists to insert into — absent in the post-publish edit dialog. */
    outlineSlot?: React.ReactNode;
    className?: string;
};

function FieldErrors({ messages, dataPlaywrightId }: { messages: string[]; dataPlaywrightId: string }) {
    if (messages.length === 0) {
        return null;
    }
    return (
        <ul className="mt-1 space-y-0.5 text-xs text-destructive-text" data-playwright-id={dataPlaywrightId}>
            {messages.map((message) => (
                <li key={message}>{message}</li>
            ))}
        </ul>
    );
}

/**
 * The REVIEW chip's details form: what is being reviewed, and how the author rates it.
 *
 * <p>Rendered into `PostFacetChipRow`'s `panel` slot, which has existed unused since the QUESTION
 * slice specifically for a facet with a payload. QUESTION is a pure toggle; REVIEW is the first facet
 * that carries one, so this is where the two shapes diverge.
 *
 * <p><b>The two fields travel together</b> (technical design decision 4). Neither is publishable
 * alone: a subject without a rating is an unfinished review, and a rating without a subject has
 * nothing to be about. The server enforces that at publish and the errors land here; this component
 * deliberately does NOT enforce it locally, because a half-filled DRAFT is legal and blocking the
 * second field until the first is set would make picking them in either order impossible.
 */
export default function ReviewFacetFields({
    localizer,
    value,
    onChange,
    groupPublicId,
    disabled = false,
    subjectErrors = [],
    ratingErrors = [],
    outlineSlot,
    className,
}: ReviewFacetFieldsProps) {
    const handleSubjectChange = React.useCallback(
        (subject: SubjectSelection | null) => onChange({ ...value, subject }),
        [onChange, value],
    );
    const handleRatingChange = React.useCallback(
        (ratingHalfStars: number | null) => onChange({ ...value, ratingHalfStars }),
        [onChange, value],
    );

    return (
        <div
            className={cn("mt-3 flex flex-col gap-4 rounded-2xl border border-border p-3", className)}
            data-playwright-id="review-facet-fields"
        >
            <div>
                <SubjectCombobox
                    localizer={localizer}
                    value={value.subject}
                    onChange={handleSubjectChange}
                    groupPublicId={groupPublicId}
                    disabled={disabled}
                />
                <FieldErrors messages={subjectErrors} dataPlaywrightId="review-subject-errors" />
            </div>

            <div>
                <HalfStarRatingInput
                    localizer={localizer}
                    value={value.ratingHalfStars}
                    onChange={handleRatingChange}
                    disabled={disabled}
                />
                <FieldErrors messages={ratingErrors} dataPlaywrightId="review-rating-errors" />
            </div>

            {outlineSlot}
        </div>
    );
}
